import React from 'react';
import { Button } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useNotifications } from '../../context/NotificationContext';
import MarkdownBlock from './blocks/MarkdownBlock';
import XoeBlockRenderer from './blocks/XoeBlockRenderer';
import XoeActionCardList from './actions/XoeActionCardList';
import { ReasoningRow } from './XoeTurnActivity';
import type { XoeMessageBubbleProps } from '../../types/xoe/XoeUiTypes';

const XoeMessageBubble: React.FC<XoeMessageBubbleProps> = ({
	message,
	patientId,
	conversationUuid,
	precedingUserText,
	onAutoContinue,
	optimistic = false,
}) => {
	const { showSuccess } = useNotifications();
	const isUser = message.role === 'user';
	const isError = !isUser && message.status === 'error';

	const handleCopy = () => {
		void navigator.clipboard.writeText(message.content);
		showSuccess('Copied');
	};

	const bubbleClass = `mg-xoe-bubble${isError ? ' is-error' : ''}${optimistic ? ' is-optimistic' : ''}`;

	return (
		<div
			className={`mg-xoe-msg mg-xoe-msg--${isUser ? 'user' : 'assistant'}`}
			data-testid={optimistic ? 'xoe-optimistic-message' : 'xoe-message'}
			data-role={message.role}
			data-status={message.status || undefined}
		>
			<div className="mg-xoe-msg-label">{isUser ? 'You' : message.persona || 'Xoe'}</div>

			<div
				className={bubbleClass}
				data-testid={isError ? 'xoe-message-error' : undefined}
				data-status={message.status || undefined}
			>
				{/* Colour alone is not an explanation. */}
				{isError && <span className="mg-xoe-bubble-flag">Xoe couldn't complete this turn</span>}

				{/* Replayed reasoning stays collapsed so it does not bury the answer. */}
				{!isUser && message.reasoning && message.reasoning.length > 0 && (
					<div className="mg-xoe-bubble-reasoning" data-testid="xoe-message-reasoning">
						{message.reasoning.map((entry) => (
							<ReasoningRow key={entry.index} entry={entry} />
						))}
					</div>
				)}

				{/* User text stays plain so typed characters like '*' are never reinterpreted. */}
				{message.content &&
					(isUser ? (
						<span className="mg-xoe-bubble-text">{message.content}</span>
					) : (
						<MarkdownBlock text={message.content} />
					))}

				{message.content_blocks && message.content_blocks.length > 0 && (
					<div className="mg-xoe-bubble-blocks">
						<XoeBlockRenderer blocks={message.content_blocks} patientId={patientId} />
					</div>
				)}

				{message.tool_calls && message.tool_calls.length > 0 && (
					<div className="mg-xoe-bubble-actions">
						<XoeActionCardList
							actions={message.tool_calls}
							messageId={message.id}
							conversationUuid={conversationUuid}
							patientId={patientId}
							precedingUserText={precedingUserText}
							onAutoContinue={onAutoContinue}
						/>
					</div>
				)}
			</div>

			{!optimistic && (
				<div className="mg-xoe-msg-foot">
					<span>{dayjs(message.created_at).format('HH:mm')}</span>
					{message.content && (
						<Button
							className="mg-xoe-copy mg-xoe-focus-ring"
							type="text"
							size="small"
							icon={<CopyOutlined />}
							onClick={handleCopy}
							aria-label="Copy message"
							data-testid="xoe-message-copy"
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default XoeMessageBubble;
